import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import QuadrilateralLab from './QuadrilateralLab';
import RationalNumberLab from './RationalNumberLab';
import PythonPlayground from './PythonPlayground';
import CoordinatePlaneLab from './CoordinatePlaneLab';
import ScratchLab from './ScratchLab';
import '../styles/moduleViewer.css';
import { getModuleById } from '../services/moduleService';
import { useCurrentUser } from '../contexts/UserContext';

const CANVAS_TOOLS = {
  'quadrilateral-lab': QuadrilateralLab,
  'rational-number-lab': RationalNumberLab,
  'python-lab': PythonPlayground,
  'coordinate-plane-lab': CoordinatePlaneLab,
  'scratch-lab': ScratchLab,
};

const sanitizeInlineHTML = (input) => {
  if (!input) return '';
  if (typeof document === 'undefined') return input;
  const ALLOWED = new Set([
    'a', 'abbr', 'b', 'blockquote', 'br', 'code', 'cite', 'em', 'i', 'p', 'span', 'strong',
    'sub', 'sup', 'u', 'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr'
  ]);
  const SAFE_ATTRS = new Set(['href', 'title', 'target', 'rel', 'style', 'colspan', 'rowspan', 'scope', 'border']);
  const template = document.createElement('template');
  template.innerHTML = input;

  const cleanNode = (node) => {
    if (!node) return null;
    const nodeType = node.nodeType;
    if (nodeType === Node.TEXT_NODE) return node;
    if (nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
      const fragment = document.createDocumentFragment();
      Array.from(node.childNodes).forEach((child) => {
        const cleanChild = cleanNode(child);
        if (cleanChild) fragment.appendChild(cleanChild);
      });
      return fragment;
    }
    if (nodeType !== Node.ELEMENT_NODE) {
      return null;
    }
    const tag = node.tagName.toLowerCase();
    if (!ALLOWED.has(tag)) {
      const fragment = document.createDocumentFragment();
      Array.from(node.childNodes).forEach((child) => {
        const cleanChild = cleanNode(child);
        if (cleanChild) fragment.appendChild(cleanChild);
      });
      return fragment;
    }
    const element = node;
    const attrs = Array.from(element.attributes);
    attrs.forEach((attr) => element.removeAttribute(attr.name));
    attrs.forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (!SAFE_ATTRS.has(name) || name.startsWith('on')) return;
      if (name === 'href') {
        const href = attr.value.trim().toLowerCase();
        if (href.startsWith('javascript:') || href.startsWith('data:')) return;
        element.setAttribute('target', '_blank');
        element.setAttribute('rel', 'noopener noreferrer');
      }
      if (name === 'style') {
        const lowered = attr.value.toLowerCase();
        if (lowered.includes('expression') || lowered.includes('javascript:')) return;
      }
      element.setAttribute(name, attr.value);
    });
    Array.from(element.childNodes).forEach((child) => {
      const cleanChild = cleanNode(child);
      if (cleanChild !== child) {
        if (cleanChild) {
          element.replaceChild(cleanChild, child);
        } else {
          element.removeChild(child);
        }
      }
    });
    return element;
  };

  const cleaned = cleanNode(template.content);
  const wrapper = document.createElement('div');
  if (cleaned) wrapper.appendChild(cleaned);
  return wrapper.innerHTML;
};

const useSanitizedHtml = (rawHtml) => useMemo(() => sanitizeInlineHTML(rawHtml), [rawHtml]);


function ModuleViewer() {
  const navigate = useNavigate();
  const { moduleId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isPresenterMode = searchParams.get('presenter') !== '0';

  // Default to presenter mode — set the param on first load if not explicitly opted out
  useEffect(() => {
    if (searchParams.get('presenter') === null) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('presenter', '1');
        return next;
      }, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [moduleData, setModuleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeBlockIndex, setActiveBlockIndex] = useState(0);
  const [activeSupportTab, setActiveSupportTab] = useState('ai');

  // Ask AI state
  const currentUser = useCurrentUser();
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiStreaming, setAiStreaming] = useState(false);
  const aiLogRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setModuleData(null);
    getModuleById(moduleId)
      .then((data) => {
        if (cancelled) return;
        setModuleData(data?.module || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Module unavailable.');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [moduleId]);

  // Flatten blocks and build sub-module groups with boundary indices.
  const { flatBlocks, submoduleGroups } = useMemo(() => {
    const blocks = [];
    const groups = [];
    let idx = 0;
    for (const submodule of moduleData?.submodules || []) {
      const startIndex = idx;
      for (const block of submodule.blocks || []) {
        blocks.push({ ...block, submoduleTitle: submodule.title });
        idx += 1;
      }
      if (idx > startIndex) {
        groups.push({ id: submodule.id, title: submodule.title, startIndex, endIndex: idx - 1 });
      }
    }
    return { flatBlocks: blocks, submoduleGroups: groups };
  }, [moduleData]);

  useEffect(() => {
    setActiveBlockIndex(0);
  }, [moduleData]);

  const totalBlocks = flatBlocks.length;
  const currentBlock = flatBlocks[activeBlockIndex];
  const canGoPrevious = activeBlockIndex > 0;
  const canGoNext = activeBlockIndex < totalBlocks - 1;

  // Sub-module group the active block belongs to.
  const currentGroup = submoduleGroups.find(
    (g) => activeBlockIndex >= g.startIndex && activeBlockIndex <= g.endIndex
  );
  const blockInSubmodule = activeBlockIndex - (currentGroup?.startIndex ?? 0) + 1;
  const blocksInSubmodule = currentGroup ? currentGroup.endIndex - currentGroup.startIndex + 1 : 1;
  const currentGroupIndex = submoduleGroups.indexOf(currentGroup);

  const handlePrevious = useCallback(() => {
    if (canGoPrevious) setActiveBlockIndex((i) => i - 1);
  }, [canGoPrevious]);

  const handleNext = useCallback(() => {
    if (canGoNext) setActiveBlockIndex((i) => i + 1);
  }, [canGoNext]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') return;
      if (e.target.closest('.cm-editor')) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); handleNext(); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); handlePrevious(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleNext, handlePrevious]);

  // Auto-scroll AI log to bottom when messages update
  useEffect(() => {
    if (aiLogRef.current) {
      aiLogRef.current.scrollTop = aiLogRef.current.scrollHeight;
    }
  }, [aiMessages]);

  const handleAiSend = useCallback(async () => {
    const text = aiInput.trim();
    if (!text || aiStreaming) return;

    const userMsg = { id: Date.now(), role: 'user', content: text };
    const assistantMsgId = Date.now() + 1;
    setAiMessages((prev) => [
      ...prev,
      userMsg,
      { id: assistantMsgId, role: 'assistant', content: '' },
    ]);
    setAiInput('');
    setAiStreaming(true);

    const blockContent = currentBlock
      ? (currentBlock.html || currentBlock.steps?.map((s) => s.html || s.text || '').join(' ') || '')
      : '';

    const context = {
      grade: moduleData?.grade ?? '',
      subject: moduleData?.subject ?? '',
      moduleTitle: moduleData?.title ?? '',
      submoduleTitle: currentGroup?.title ?? '',
      blockHeading: currentBlock?.heading ?? '',
      blockContent,
      firstName: currentUser?.firstName ?? 'Student',
    };

    // Build history from current messages (exclude the just-added assistant placeholder)
    const history = aiMessages.map((m) => ({ role: m.role, content: m.content }));

    try {
      const res = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, context, history }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Request failed.' }));
        setAiMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsgId ? { ...m, content: err.error || 'Something went wrong.' } : m
          )
        );
        setAiStreaming(false);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete line
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6).trim();
          if (payload === '[DONE]') break;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.text) {
              setAiMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId ? { ...m, content: m.content + parsed.text } : m
                )
              );
            }
            if (parsed.error) {
              setAiMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId ? { ...m, content: parsed.error } : m
                )
              );
            }
          } catch {
            // ignore malformed SSE line
          }
        }
      }
    } catch (err) {
      setAiMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId
            ? { ...m, content: 'Connection error. Please try again.' }
            : m
        )
      );
    } finally {
      setAiStreaming(false);
    }
  }, [aiInput, aiStreaming, aiMessages, moduleData, currentGroup, currentBlock, currentUser]);

  if (loading) {
    return (
      <div className="module-viewer-shell">
        <header className="module-viewer-header">
          <div>
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/learn')}>
              ← Back to Learn
            </button>
            <h1>Loading module…</h1>
            <p className="muted-text">Fetching the latest content for you.</p>
          </div>
        </header>
      </div>
    );
  }

  if (!moduleData) {
    return (
      <div className="module-viewer-shell">
        <header className="module-viewer-header">
          <div>
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/learn')}>
              ← Back to Learn
            </button>
            <h1>Module unavailable</h1>
            <p className="muted-text">
              {error || 'We could not find that module. Head back to pick another lesson.'}
            </p>
          </div>
        </header>
      </div>
    );
  }

  const moduleTitle = moduleData.title || 'Module';
  const moduleSubject = moduleData.subject || 'Module';
  const moduleVersion = moduleData.version;

  const handleTogglePresenter = () => {
    const next = new URLSearchParams(searchParams);
    next.set('presenter', isPresenterMode ? '0' : '1');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="module-viewer-shell">
      {!isPresenterMode && (
        <header className="module-viewer-header">
          <div className="module-viewer-title">
            <p className="eyebrow">
              {moduleSubject}
              {moduleVersion ? ` · ${moduleVersion}` : ''}
            </p>
            <h2 className="module-viewer-heading">{moduleTitle}</h2>
          </div>
          <div className="module-viewer-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleTogglePresenter}
            >
              Presenter View
            </button>
            <button type="button" className="btn btn-secondary close-btn" onClick={() => navigate('/learn')} aria-label="Close module viewer">
              <svg viewBox="0 0 24 24" className="module-close-icon" aria-hidden="true" focusable="false">
                <path
                  d="M6 6l12 12M18 6 6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="close-label">Close</span>
            </button>
          </div>
        </header>
      )}

      <div className="module-viewer-body">
        <section className="module-viewer-content" aria-live="polite">

          {/* Content panel header: breadcrumb + presenter actions */}
          <div className="content-panel-header">
            <nav className="block-breadcrumb" aria-label="Module breadcrumb">
              <span className="breadcrumb-module">{moduleTitle}</span>
              <span className="breadcrumb-sep">›</span>
              <span className="breadcrumb-submodule">{currentGroup?.title}</span>
              <span className="breadcrumb-sep">›</span>
              <span className="breadcrumb-block">Block {blockInSubmodule} of {blocksInSubmodule}</span>
            </nav>
            {isPresenterMode && (
              <div className="presenter-toolbar">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleTogglePresenter}
                >
                  Exit Presenter View
                </button>
                <button type="button" className="btn btn-secondary close-btn" onClick={() => navigate('/learn')} aria-label="Close module viewer">
                  <svg viewBox="0 0 24 24" className="module-close-icon" aria-hidden="true" focusable="false">
                    <path
                      d="M6 6l12 12M18 6 6 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="close-label">Close</span>
                </button>
              </div>
            )}
          </div>

          {/* Block */}
          <div className="module-blocks">
            {currentBlock && (
              <BlockRenderer
                key={currentBlock.id}
                block={currentBlock}
                moduleId={moduleId}
                nextSubmodule={
                  activeBlockIndex === currentGroup?.endIndex
                    ? submoduleGroups[currentGroupIndex + 1] ?? null
                    : null
                }
                onAdvance={() => setActiveBlockIndex(activeBlockIndex + 1)}
              />
            )}
          </div>

          {/* Footer: segmented progress + navigation */}
          {totalBlocks > 0 && (
            <div className="submodule-footer">
              <div className="footer-progress" aria-label="Progress through module">
                <div className="segmented-progress">
                  {submoduleGroups.map((group, gi) => {
                    const groupSize = group.endIndex - group.startIndex + 1;
                    const isActive = gi === currentGroupIndex;
                    const isComplete = activeBlockIndex > group.endIndex;
                    const completedInGroup = isComplete
                      ? groupSize
                      : isActive
                        ? activeBlockIndex - group.startIndex + 1
                        : 0;
                    return (
                      <div
                        key={group.id}
                        className={`progress-segment ${isActive ? 'is-active' : ''} ${isComplete ? 'is-complete' : ''}`}
                        style={{ flex: groupSize }}
                        title={group.title}
                      >
                        <div
                          className="progress-segment-fill"
                          style={{ width: `${(completedInGroup / groupSize) * 100}%` }}
                        />
                      </div>
                    );
                  })}
                </div>
                <span className="progress-label">
                  {currentGroupIndex + 1}/{submoduleGroups.length} · {currentGroup?.title}
                </span>
              </div>
              <div className="footer-actions">
                <button
                  type="button"
                  className="btn btn-secondary block-nav-arrow"
                  onClick={handlePrevious}
                  disabled={!canGoPrevious}
                  aria-label="Previous block"
                >←</button>
                <button
                  type="button"
                  className="btn btn-secondary block-nav-arrow"
                  onClick={handleNext}
                  disabled={!canGoNext}
                  aria-label="Next block"
                >→</button>
              </div>
            </div>
          )}
        </section>

        <aside className="module-viewer-rail">
          <div className="support-nav" role="tablist" aria-label="Support options">
            {[
              { id: 'ai', label: 'Ask AI' },
              { id: 'mentor', label: 'Ask Mentor' },
              { id: 'notes', label: 'Notes' },
            ].map((tab, index, arr) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeSupportTab === tab.id}
                className={`support-nav-btn ${activeSupportTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveSupportTab(tab.id)}
                data-first={index === 0}
                data-last={index === arr.length - 1}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="rail-card support-panel" role="tabpanel">
            {activeSupportTab === 'ai' && (
              <div className="support-body">
                <div className="ai-terminal">
                  <div className="ai-terminal-header">
                    <span className={`status-dot ${aiStreaming ? 'is-streaming' : ''}`} />
                    <span>Ask AI · {moduleData?.subject} · Grade {moduleData?.grade}</span>
                  </div>
                  <div className="ai-terminal-log" ref={aiLogRef}>
                    {aiMessages.length === 0 ? (
                      <p className="muted-text ai-empty-hint">
                        Ask me anything about <strong>{currentBlock?.heading || currentGroup?.title || 'this topic'}</strong> — summaries, examples, hints, or deeper explanations.
                      </p>
                    ) : (
                      aiMessages.map((msg) => (
                        <div key={msg.id} className={`ai-message ai-message--${msg.role}`}>
                          <span className="ai-message-bubble">
                            {msg.content || (msg.role === 'assistant' && aiStreaming ? <span className="ai-cursor" /> : null)}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="ai-terminal-input">
                    <textarea
                      rows="3"
                      placeholder="Ask a question about this topic…"
                      value={aiInput}
                      onChange={(e) => setAiInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleAiSend();
                        }
                      }}
                      disabled={aiStreaming}
                    />
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleAiSend}
                      disabled={aiStreaming || !aiInput.trim()}
                    >
                      {aiStreaming ? 'Thinking…' : 'Send'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {activeSupportTab === 'mentor' && (
              <div className="support-body mentor-body">
                <p className="eyebrow">Ask Mentor</p>
                <p className="muted-text">Draft a note to your mentor and we&apos;ll queue it.</p>
                <textarea rows="6" placeholder="Hi mentor, I&apos;m stuck on..." />
                <button type="button" className="btn btn-secondary">Request help</button>
              </div>
            )}
            {activeSupportTab === 'notes' && (
              <div className="support-body notes-body">
                <p className="eyebrow">Notes</p>
                <textarea rows="8" placeholder="Capture personal notes here." />
                <button type="button" className="btn btn-secondary">Save note</button>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function BlockRenderer({ block, nextSubmodule, onAdvance, moduleId }) {
  switch (block.kind) {
    case 'text':
      return <TextBlock block={block} />;
    case 'stepthrough':
      return <StepThroughBlock block={block} />;
    case 'mcq':
      return <MCQBlock block={block} />;
    case 'canvas':
      return <CanvasBlock block={block} moduleId={moduleId} />;
    case 'dragdrop':
      return <DragDropBlock block={block} />;
    case 'rapidfire':
      return <RapidFireBlock block={block} />;
    case 'summary':
      return <SummaryBlock block={block} nextSubmodule={nextSubmodule} onAdvance={onAdvance} />;
    case 'table':
      return <TableBlock block={block} />;
    default:
      return (
        <article className="module-block">
          {block.heading ? <h3 className="module-block-title">{block.heading}</h3> : null}
          <p className="muted-text">This block type is not supported yet.</p>
        </article>
      );
  }
}

function StepThroughBlock({ block }) {
  const steps = block.steps || [];
  const [revealed, setRevealed] = useState(1);
  const isComplete = revealed >= steps.length;

  useEffect(() => { setRevealed(1); }, [block.id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setRevealed((r) => Math.min(steps.length, r + 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setRevealed((r) => Math.max(1, r - 1)); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [steps.length]);

  return (
    <article className="module-block stepthrough-block">
      {block.heading ? <h3 className="module-block-title">{block.heading}</h3> : null}

      <div className="stepthrough-body">
        <div className="stepthrough-steps">
          {steps.slice(0, revealed).map((step, i) => (
            <StepItem key={step.id} step={step} index={i} isLatest={i === revealed - 1} />
          ))}
        </div>

        <div className="stepthrough-controls">
          <button
            type="button"
            className="stepthrough-arrow"
            onClick={() => setRevealed((r) => Math.max(1, r - 1))}
            disabled={revealed <= 1}
            aria-label="Previous step"
          >↑</button>

          <div className="stepthrough-pip-col">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`stepthrough-pip ${i < revealed ? 'filled' : ''} ${i === revealed - 1 ? 'active' : ''}`}
              />
            ))}
          </div>

          {isComplete ? (
            <span className="stepthrough-done">✓</span>
          ) : (
            <button
              type="button"
              className="stepthrough-arrow"
              onClick={() => setRevealed((r) => r + 1)}
              aria-label="Next step"
            >↓</button>
          )}
        </div>
      </div>
    </article>
  );
}

function StepItem({ step, index, isLatest }) {
  const ref = useRef(null);
  const topRef = useRef(null);
  const safeHtml = useSanitizedHtml(step.html);
  const CanvasComponent = step.tool ? CANVAS_TOOLS[step.tool] : null;

  useEffect(() => {
    if (isLatest && topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isLatest]);

  return (
    <div ref={ref} className={`stepthrough-step ${isLatest ? 'is-latest' : 'is-revealed'}`}>
      <span ref={topRef} className="step-number">{index + 1}</span>
      <div className="step-body">
        {safeHtml && <div dangerouslySetInnerHTML={{ __html: safeHtml }} />}
        {CanvasComponent && (
          <div className="step-canvas-wrap">
            <CanvasComponent {...(step.props || {})} />
          </div>
        )}
      </div>
    </div>
  );
}

function TextBlock({ block }) {
  const safeHtml = useSanitizedHtml(block.html);
  return (
    <article className="module-block">
      {block.heading ? <h3 className="module-block-title">{block.heading}</h3> : null}
      <div className="module-html" dangerouslySetInnerHTML={{ __html: safeHtml }} />
    </article>
  );
}

function CanvasBlock({ block, moduleId }) {
  const CanvasComponent = block.tool ? CANVAS_TOOLS[block.tool] : null;
  return (
    <article className="module-block">
      {block.heading ? <h3 className="module-block-title">{block.heading}</h3> : null}
      {CanvasComponent ? (
        <CanvasComponent moduleId={moduleId} blockId={block.id} {...(block.props || {})} />
      ) : (
        <p className="muted-text">Interactive tool unavailable.</p>
      )}
    </article>
  );
}

function MCQBlock({ block }) {
  const [selectedId, setSelectedId] = useState('');
  const options = useMemo(() => {
    if (!block.options) return [];
    if (!block.shuffle) return block.options;
    const cloned = [...block.options];
    for (let i = cloned.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
    }
    return cloned;
  }, [block.options, block.shuffle]);

  useEffect(() => {
    setSelectedId('');
  }, [block.id]);

  const selectedOption = options.find((opt) => opt.id === selectedId);
  const state = selectedOption ? (selectedOption.correct ? 'correct' : 'incorrect') : 'neutral';

  return (
    <article className={`module-block mcq-block mcq-${state}`}>
      {block.heading ? <h3 className="module-block-title">{block.heading}</h3> : null}
      {block.stem ? <p className="module-block-stem">{block.stem}</p> : null}
      <div className="mcq-options">
        {options.map((option) => {
          const optionState = selectedId === option.id ? (option.correct ? 'correct' : 'incorrect') : 'neutral';
          return (
            <button
              key={option.id}
              type="button"
              className={`mcq-option ${optionState}`}
              onClick={() => setSelectedId(option.id)}
            >
              <span>{option.text}</span>
            </button>
          );
        })}
      </div>
      {selectedOption && (
        <div className="mcq-feedback" role="status">
          {selectedOption.correct ? 'Nice! That is correct.' : 'Try again — revisit the definition.'}
          {!selectedOption.correct && selectedOption.hint ? (
            <p className="mcq-hint">Hint: {selectedOption.hint}</p>
          ) : null}
        </div>
      )}
    </article>
  );
}

function DragDropBlock({ block }) {
  const buckets = useMemo(() => block.buckets || [], [block.buckets]);
  const options = useMemo(() => block.options || [], [block.options]);
  const UNPLACED = '__unplaced__';
  const [placements, setPlacements] = useState(() => {
    const initial = {};
    options.forEach((option) => {
      initial[option.id] = UNPLACED;
    });
    return initial;
  });
  const [revealed, setRevealed] = useState(false);
  const [draggingId, setDraggingId] = useState('');
  const [hoverTarget, setHoverTarget] = useState('');

  useEffect(() => {
    const initial = {};
    options.forEach((option) => {
      initial[option.id] = UNPLACED;
    });
    setPlacements(initial);
    setRevealed(false);
    setDraggingId('');
    setHoverTarget('');
  }, [block.id, options]);

  const groupedOptions = useMemo(() => {
    const groups = { [UNPLACED]: [] };
    buckets.forEach((bucket) => {
      groups[bucket.id] = [];
    });
    options.forEach((option) => {
      const bucketId = placements[option.id] || UNPLACED;
      if (!groups[bucketId]) {
        groups[bucketId] = [];
      }
      groups[bucketId].push(option);
    });
    return groups;
  }, [buckets, options, placements]);

  const moveOption = (optionId, bucketId) => {
    if (!optionId || typeof bucketId === 'undefined' || placements[optionId] === bucketId) return;
    setPlacements((prev) => ({ ...prev, [optionId]: bucketId }));
    setRevealed(false);
  };

  const handleDrop = (targetId, event) => {
    event.preventDefault();
    const optionId = event.dataTransfer.getData('text/plain');
    if (optionId) {
      moveOption(optionId, targetId);
    }
    setHoverTarget('');
  };

  const handleDragOver = (targetId, event) => {
    event.preventDefault();
    if (hoverTarget !== targetId) setHoverTarget(targetId);
  };

  const handleDragLeave = (targetId) => {
    if (hoverTarget === targetId) {
      setHoverTarget('');
    }
  };

  const correctCount = options.filter((option) => option.answer && placements[option.id] === option.answer).length;

  if (!buckets.length) {
    return (
      <article className="module-block">
        {block.heading ? <h3 className="module-block-title">{block.heading}</h3> : null}
        <p className="muted-text">This activity is missing drop zones.</p>
      </article>
    );
  }

  return (
    <article className="module-block dragdrop-block">
      {block.heading ? <h3 className="module-block-title">{block.heading}</h3> : null}
      {block.stem ? <p className="module-block-stem">{block.stem}</p> : null}
      <div className="dragdrop-layout">
        <section
          className={`dragdrop-source dragdrop-column ${hoverTarget === UNPLACED ? 'is-hovered' : ''}`}
          aria-label="Available options"
        >
          <header className="dragdrop-column-header">
            <p>Options</p>
            <span className="dragdrop-count">{groupedOptions[UNPLACED]?.length || 0}</span>
          </header>
          <div
            className="dragdrop-column-body"
            onDragOver={(event) => handleDragOver(UNPLACED, event)}
            onDrop={(event) => handleDrop(UNPLACED, event)}
            onDragLeave={() => handleDragLeave(UNPLACED)}
          >
            {groupedOptions[UNPLACED]?.length ? (
              groupedOptions[UNPLACED].map((option) => (
                <div
                  key={option.id}
                  className={`dragdrop-card ${draggingId === option.id ? 'is-dragging' : ''}`}
                  draggable
                  onDragStart={(event) => {
                    event.dataTransfer.setData('text/plain', option.id);
                    setDraggingId(option.id);
                  }}
                  onDragEnd={() => {
                    setDraggingId('');
                    setHoverTarget('');
                  }}
                >
                  {option.text}
                </div>
              ))
            ) : (
              <p className="dragdrop-placeholder">All items placed</p>
            )}
          </div>
        </section>
        <div className="dragdrop-buckets">
          {buckets.map((bucket) => {
            const bucketOptions = groupedOptions[bucket.id] || [];
            return (
              <section
                key={bucket.id}
                className={`dragdrop-column ${hoverTarget === bucket.id ? 'is-hovered' : ''}`}
                aria-label={bucket.label}
              >
                <header className="dragdrop-column-header">
                  <p>{bucket.label}</p>
                  <span className="dragdrop-count">{bucketOptions.length}</span>
                </header>
                <div
                  className="dragdrop-column-body"
                  onDragOver={(event) => handleDragOver(bucket.id, event)}
                  onDrop={(event) => handleDrop(bucket.id, event)}
                  onDragLeave={() => handleDragLeave(bucket.id)}
                >
                  {bucketOptions.length === 0 ? (
                    <p className="dragdrop-placeholder">Drop items here</p>
                  ) : (
                    bucketOptions.map((option) => (
                      <div
                        key={option.id}
                        className={`dragdrop-card ${draggingId === option.id ? 'is-dragging' : ''}`}
                        draggable
                        onDragStart={(event) => {
                          event.dataTransfer.setData('text/plain', option.id);
                          setDraggingId(option.id);
                        }}
                        onDragEnd={() => {
                          setDraggingId('');
                          setHoverTarget('');
                        }}
                      >
                        {option.text}
                      </div>
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      </div>
      <div className="dragdrop-actions">
        <button type="button" className="btn btn-secondary" onClick={() => setRevealed(true)}>
          Check answers
        </button>
        {revealed && (
          <span className="dragdrop-score">
            {correctCount} / {options.length} correct
          </span>
        )}
      </div>
    </article>
  );
}

function interpolateStem(stem, vars) {
  if (!stem || !vars) return stem || '';
  return stem.replace(/\{(\w+)\}/g, (_, key) => (key in vars ? vars[key] : `{${key}}`));
}

function RapidFireBlock({ block }) {
  const questions = block.questions || [];
  const totalTime = block.timer ?? 30;

  const [phase, setPhase] = useState('ready'); // 'ready' | 'playing' | 'done'
  const [qIndex, setQIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(totalTime);
  const [score, setScore] = useState(0);
  const [flash, setFlash] = useState(null); // { optionId, correct } for brief feedback

  // Reset when block changes
  useEffect(() => {
    setPhase('ready');
    setQIndex(0);
    setTimeLeft(totalTime);
    setScore(0);
    setFlash(null);
  }, [block.id, totalTime]);

  // Countdown ticker
  useEffect(() => {
    if (phase !== 'playing') return;
    if (timeLeft <= 0) { setPhase('done'); return; }
    const id = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [phase, timeLeft]);

  const handleStart = () => {
    setQIndex(0);
    setTimeLeft(totalTime);
    setScore(0);
    setFlash(null);
    setPhase('playing');
  };

  const handleOption = useCallback((option) => {
    if (flash) return; // debounce during feedback window
    const isCorrect = !!option.correct;
    setFlash({ optionId: option.id, correct: isCorrect });
    if (isCorrect) setScore((s) => s + 1);

    setTimeout(() => {
      setFlash(null);
      const next = qIndex + 1;
      if (next >= questions.length) {
        setPhase('done');
      } else {
        setQIndex(next);
      }
    }, 500);
  }, [flash, qIndex, questions.length]);

  const currentQ = questions[qIndex];
  const timerDanger = timeLeft <= 10;
  const timerWarning = timeLeft <= 20 && timeLeft > 10;

  if (phase === 'ready') {
    return (
      <article className="module-block rapidfire-block">
        {block.heading ? <h3 className="module-block-title">{block.heading}</h3> : null}
        <div className="rapidfire-stem">
          <span className="rapidfire-stem-text">{block.stem}</span>
          {block.example && (
            <span className="rapidfire-stem-example">e.g. {block.example}</span>
          )}
        </div>
        <p className="rapidfire-ready-meta">{questions.length} questions · {totalTime}s</p>
        <button type="button" className="btn btn-secondary rapidfire-start-btn" onClick={handleStart}>
          Start
        </button>
      </article>
    );
  }

  if (phase === 'done') {
    const pct = questions.length > 0 ? Math.round((score / questions.length) * 100) : 0;
    return (
      <article className="module-block rapidfire-block">
        {block.heading ? <h3 className="module-block-title">{block.heading}</h3> : null}
        <div className="rapidfire-done">
          <p className="rapidfire-done-score">{score}<span className="rapidfire-done-total"> / {questions.length}</span></p>
          <p className="rapidfire-done-pct">{pct}% correct</p>
          <button type="button" className="btn btn-secondary" onClick={handleStart}>
            Try again
          </button>
        </div>
      </article>
    );
  }

  // playing
  const optionCount = currentQ?.options?.length ?? 0;

  return (
    <article className="module-block rapidfire-block">
      {block.heading ? <h3 className="module-block-title">{block.heading}</h3> : null}

      <div className="rapidfire-hud">
        <span className="rapidfire-progress">{qIndex + 1} / {questions.length}</span>
        <span className={`rapidfire-timer ${timerDanger ? 'is-danger' : timerWarning ? 'is-warning' : ''}`}>
          {timeLeft}s
        </span>
      </div>

      <div className="rapidfire-stem">
        <span className="rapidfire-stem-text">{block.stem}</span>
        {block.example && (
          <span className="rapidfire-stem-example">e.g. {block.example}</span>
        )}
      </div>

      {currentQ?.description && (
        <div className="rapidfire-description">{currentQ.description}</div>
      )}

      <div className={`rapidfire-options rapidfire-options--${optionCount}`}>
        {currentQ?.options?.map((option) => {
          const isSelected = flash?.optionId === option.id;
          const stateClass = isSelected ? (flash.correct ? 'is-correct' : 'is-incorrect') : '';
          return (
            <button
              key={option.id}
              type="button"
              className={`rapidfire-option ${stateClass}`}
              onClick={() => handleOption(option)}
              disabled={!!flash}
            >
              {option.text}
            </button>
          );
        })}
      </div>
    </article>
  );
}

function TableBlock({ block }) {
  const columns = block.columns || [];
  const rows = block.rows || [];
  const [revealed, setRevealed] = useState(1);
  const isComplete = revealed >= rows.length;
  const safeHtml = useSanitizedHtml(block.html);

  useEffect(() => { setRevealed(1); }, [block.id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setRevealed((r) => Math.min(rows.length, r + 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setRevealed((r) => Math.max(1, r - 1)); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rows.length]);

  if (rows.length === 0) {
    return (
      <article className="module-block table-block">
        {block.heading ? <h3 className="module-block-title">{block.heading}</h3> : null}
        {safeHtml ? <div className="module-html" dangerouslySetInnerHTML={{ __html: safeHtml }} /> : null}
        <div className="table-scroll">
          <table>
            {block.caption ? <caption>{block.caption}</caption> : null}
            <tbody>
              <tr><td colSpan={columns.length || 1} className="muted-text">No data available.</td></tr>
            </tbody>
          </table>
        </div>
      </article>
    );
  }

  return (
    <article className="module-block table-block stepthrough-block">
      {block.heading ? <h3 className="module-block-title">{block.heading}</h3> : null}
      {safeHtml ? <div className="module-html" dangerouslySetInnerHTML={{ __html: safeHtml }} /> : null}
      <div className="stepthrough-body">
        <div className="table-scroll">
          <table>
            {block.caption ? <caption>{block.caption}</caption> : null}
            {columns.length > 0 && (
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column} scope="col">{column}</th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={rowIndex === revealed - 1 ? 'table-row-latest' : 'table-row-revealed'}
                  style={rowIndex >= revealed ? { visibility: 'collapse' } : undefined}
                >
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} dangerouslySetInnerHTML={{ __html: sanitizeInlineHTML(cell) }} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="stepthrough-controls">
          <button
            type="button"
            className="stepthrough-arrow"
            onClick={() => setRevealed((r) => Math.max(1, r - 1))}
            disabled={revealed <= 1}
            aria-label="Previous row"
          >↑</button>

          <div className="stepthrough-pip-col">
            {rows.map((_, i) => (
              <span
                key={i}
                className={`stepthrough-pip ${i < revealed ? 'filled' : ''} ${i === revealed - 1 ? 'active' : ''}`}
              />
            ))}
          </div>

          {isComplete ? (
            <span className="stepthrough-done">✓</span>
          ) : (
            <button
              type="button"
              className="stepthrough-arrow"
              onClick={() => setRevealed((r) => r + 1)}
              aria-label="Next row"
            >↓</button>
          )}
        </div>
      </div>
    </article>
  );
}

function SummaryBlock({ block, nextSubmodule, onAdvance }) {
  const bullets = block.bullets || [];
  const totalSteps = bullets.length + (nextSubmodule ? 1 : 0);
  const [revealed, setRevealed] = useState(1);
  const allRevealed = revealed >= totalSteps;
  const showTransition = nextSubmodule && revealed > bullets.length;

  useEffect(() => { setRevealed(1); }, [block.id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setRevealed((r) => Math.min(totalSteps, r + 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setRevealed((r) => Math.max(1, r - 1)); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [totalSteps]);

  return (
    <article className="module-block stepthrough-block">
      {block.heading ? <h3 className="module-block-title">{block.heading}</h3> : null}
      <div className="stepthrough-body">
        <div className="summary-steps">
          {bullets.slice(0, Math.min(revealed, bullets.length)).map((bullet, i) => (
            <SummaryBullet key={i} text={bullet} isLatest={i === revealed - 1} />
          ))}
          {showTransition && (
            <TransitionCard
              fromTitle={block.submoduleTitle}
              toTitle={nextSubmodule.title}
              onAdvance={onAdvance}
              isLatest={revealed > bullets.length}
            />
          )}
        </div>

        <div className="stepthrough-controls">
          <button
            type="button"
            className="stepthrough-arrow"
            onClick={() => setRevealed((r) => Math.max(1, r - 1))}
            disabled={revealed <= 1}
            aria-label="Previous bullet"
          >↑</button>

          <div className="stepthrough-pip-col">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <span
                key={i}
                className={`stepthrough-pip ${i < revealed ? 'filled' : ''} ${i === revealed - 1 ? 'active' : ''}`}
              />
            ))}
          </div>

          {allRevealed ? (
            <span className="stepthrough-done">✓</span>
          ) : (
            <button
              type="button"
              className="stepthrough-arrow"
              onClick={() => setRevealed((r) => r + 1)}
              aria-label="Next bullet"
            >↓</button>
          )}
        </div>
      </div>
    </article>
  );
}

function TransitionCard({ fromTitle, toTitle, onAdvance, isLatest }) {
  const ref = useRef(null);

  useEffect(() => {
    if (isLatest && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isLatest]);

  return (
    <div ref={ref} className="summary-transition" role="status">
      <div className="transition-badge">Section complete</div>
      <p className="transition-from-name">{fromTitle} <span className="transition-tick">✓</span></p>
      <div className="transition-arrow">↓</div>
      <p className="transition-label">Up next</p>
      <h2 className="transition-to">{toTitle}</h2>
      <button type="button" className="btn btn-secondary summary-continue-btn" onClick={onAdvance}>
        Continue →
      </button>
    </div>
  );
}

function SummaryBullet({ text, isLatest }) {
  const ref = useRef(null);

  useEffect(() => {
    if (isLatest && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isLatest]);

  return (
    <div ref={ref} className={`stepthrough-step ${isLatest ? 'is-latest' : 'is-revealed'}`}>
      <span className="step-number summary-bullet-dot" aria-hidden="true">•</span>
      <div className="step-body">{text}</div>
    </div>
  );
}

export default ModuleViewer;
